import React, { useEffect, useState } from "react"
import MetaTags from "react-meta-tags"
import {
    Container,
    Row,
    Col,
    Card,
    CardBody
} from "reactstrap"
//Import Breadcrumb
import Breadcrumbs from "components/Common/Breadcrumb"
import Datatable from "components/Datatables/datatable"
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { get } from "helpers/api_helper"
import moment from "moment";

const isType = 'Invoice Summary'
const styleSheet = {
    fontStyle: {
        fontWeight: 'bold',
        background: '#424141',
        padding: '0px 15px',
        color: '#fff'
    }
}

const InvoiceSummary = () => {
    const [rowData, setRowData] = useState(null)
    const [isLoading, setIsLoading] = useState(false)
    const [filterData, setFilterData] = useState({
        'client': '',
        'startdate': new Date(),
        'enddate': new Date(),
        'type': 0
    });

    useEffect(() => {
        getInvoiceData();
    }, [])

    // Calculate Totals
    const calculateTotals = () => {
        let totals = {
            basic: 0,
            sgst: 0,
            cgst: 0,
            igst: 0,
            total: 0,
            paid: 0,
            balance: 0
        }
        if (rowData?.data) {
            rowData.data.forEach(item => {
                totals.basic += Number(item.basic_amount || 0)
                totals.sgst += Number(item.sgst || 0)
                totals.cgst += Number(item.cgst || 0)
                totals.igst += Number(item.igst || 0)
                totals.total += Number(item.total_amount || 0)
                totals.paid += Number(item.paid || 0)
                totals.balance += Number(item.balance || 0)
            })
        }
        return totals
    }

    const totals = calculateTotals()

    // table Column Data
    let tableColumns = [
        {
            dataField: 'id',
            text: '#',
            sort: true,
            formatter: (cell, row, rowIndex) => rowIndex + 1,
            footer: ''
        }, {
            dataField: 'invoice_number',
            text: 'Invoice Number',
            sort: true,
            footer: 'Total'
        }, {
            dataField: 'invoice_date',
            text: 'Invoice Date',
            sort: true,
            formatter: (cell, row) => moment(row.invoice_date).format('DD-MM-YYYY'),
            csvFormatter: (cell, row) => moment(row.invoice_date).format('DD-MM-YYYY'),
            footer: ''
        }, {
            dataField: 'client',
            text: 'Client',
            sort: true,
            footer: ''
        }, {
            dataField: 'due_date',
            text: 'Due Date',
            sort: true,
            formatter: (cell, row) => moment(row.due_date).format('DD-MM-YYYY'),
            csvFormatter: (cell, row) => moment(row.due_date).format('DD-MM-YYYY'),
            footer: ''
        }, {
            dataField: 'gstin',
            text: 'GSTIN',
            sort: true,
            footer: ''
        }, {
            dataField: 'basic_amount',
            text: 'Basic Amount',
            sort: true,
            formatter: (cell, row) => Number(row.basic_amount).toFixed(2),
            csvFormatter: (cell, row) => Number(row.basic_amount).toFixed(2),
            footer: totals.basic.toFixed(2)
        }, {
            dataField: 'gst_rate',
            text: 'GST Rate',
            sort: false,
            formatter: (cell, row) => Number(row.gst_rate) + '%',
            csvFormatter: (cell, row) => Number(row.gst_rate) + '%',
            footer: ''
        }, {
            dataField: 'sgst',
            text: 'SGST',
            sort: true,
            formatter: (cell, row) => Number(row.sgst).toFixed(2),
            csvFormatter: (cell, row) => Number(row.sgst).toFixed(2),
            footer: totals.sgst.toFixed(2)
        }, {
            dataField: 'cgst',
            text: 'CGST',
            sort: true,
            formatter: (cell, row) => Number(row.cgst).toFixed(2),
            csvFormatter: (cell, row) => Number(row.cgst).toFixed(2),
            footer: totals.cgst.toFixed(2)
        }, {
            dataField: 'igst',
            text: 'IGST',
            sort: true,
            formatter: (cell, row) => Number(row.igst).toFixed(2),
            csvFormatter: (cell, row) => Number(row.igst).toFixed(2),
            footer: totals.igst.toFixed(2)
        }, {
            dataField: 'total_amount',
            text: 'Total Amount',
            sort: true,
            formatter: (cell, row) => Number(row.total_amount).toFixed(2),
            csvFormatter: (cell, row) => Number(row.total_amount).toFixed(2),
            footer: totals.total.toFixed(2)
        }, {
            dataField: 'paid',
            text: 'Paid',
            sort: true,
            formatter: (cell, row) => Number(row.paid).toFixed(2),
            csvFormatter: (cell, row) => Number(row.paid).toFixed(2),
            footer: totals.paid.toFixed(2)
        }, {
            dataField: 'balance',
            text: 'Balance',
            sort: true,
            formatter: (cell, row) => Number(row.balance).toFixed(2),
            csvFormatter: (cell, row) => Number(row.balance).toFixed(2),
            footer: totals.balance.toFixed(2)
        }, {
            dataField: 'actions',
            text: "Actions",
            isDummyField: true,
            csvExport: false,
            formatter: (cell, row) => {
                return <>
                    <span
                        onClick={() => window.open("/invoice/print?invoice_number=" + row.invoice_number, "_blank")}
                        className="btn btn-outline-secondary btn-sm edit"
                        title="View Invoice" >
                        <i className="fas fa-download"></i>
                    </span>
                </>
            }
        }
    ];


    const getInvoiceData = async (date = filterData.startdate) => {
        setIsLoading(true)
        const month = moment(date).format('M')
        const year = moment(date).format('YYYY')
        const res = await get(`api/reports/invoicesummary?month=${month}&year=${year}`);
        if (res.status == 200) {
            setRowData(res.response);
            setIsLoading(false)
        }
    }

    return (
        <React.Fragment>
            <div className="page-content">
                <MetaTags> <title>Dashboard | {isType}</title> </MetaTags>
                <Container fluid>
                    <Breadcrumbs title="Reports" breadcrumbItem={isType} />
                    <Row>
                        <Col>
                            <Card>
                                <CardBody>
                                    <Row>
                                        <Col className={'mb-3'} md={3}>
                                            <label>Select Month & Year</label>
                                            <DatePicker
                                                className="form-control"
                                                selected={filterData.startdate}
                                                onChange={(date) => {
                                                    setFilterData(prev => ({
                                                        ...prev,
                                                        startdate: date,
                                                        enddate: date
                                                    }));
                                                    getInvoiceData(date)
                                                }}
                                                showMonthYearPicker
                                                dateFormat="MM/yyyy"
                                            />
                                        </Col>
                                    </Row>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>

                    <Row>
                        <Col xl="12">
                            <Card>
                                <CardBody>
                                    <Datatable
                                        keyField={'id'}
                                        title={`All ${isType}`}
                                        filename={`Invoice_Summary_${moment(filterData.startdate).format('MM_YYYY')}`}
                                        isAdd={false}
                                        handleAddButton=''
                                        isPagination={false}
                                        // pdf={true}
                                        excel={true}
                                        csv={false}
                                        is_remote={false}
                                        columns={tableColumns}
                                        rows={rowData?.data || []}
                                        isTableHead={true}
                                        loading={isLoading}
                                    />
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
        </React.Fragment>
    )
}

export default InvoiceSummary
